import { IShowable } from "../interfaces/IShowable";


/**
 * Events that this class or other classes might use for their Show/Hide event dispatching (if applicable)
 */
export enum ShowableEvents {
    SHOW_START = "showStart",
    SHOW_COMPLETE = "showComplete",
    HIDE_START = "hideSart",
    HIDE_COMPLETE = "hideComplete",
}

/**
 * NOTE: you cann have onStart and onComplete with both Show and Hide 
 *       included inside the props objects:
 * @see this.showTweenConfig.onStart|onComplete
 * @see this.hideTweenConfig.onStart|onComplete
 */
export class Showable
    extends Phaser.GameObjects.Container
// removed IShowable: 
// - this Mixin doesnt use onComplete in Show/Hide and 
//   uses useShowTweenTargetProps in Show instead (removed for incompatibility 
//   with other systems that dont use Showable Mixin, but do use old IShowable implementation)
// implements IShowable  
{

    /**
     * Default Mixin's show tween config props
     * [static] Doesn't disappear when merging the Mixin
     */
    public static defaultShowTweenConfig = {
        duration: 500
    }
    /**
     * Default Mixin's show tween config props
     * [static] Doesn't disappear when merging the Mixin
     */
    public static defaultHideTweenConfig = {
        duration: 500
    }
    public static defaultShowTweenTargetProps = {
        alpha: 1
    }
    public static defaultHideTweenTargetProps = {
        alpha: 0
    }
    /**
     * Default props to set to before starting to Show
     */
    // public static defaultFromShowTweenTargetProps = {
    //     alpha: 0
    // }

    // values set here are undefined when mixed into target
    // Solution: override in target's Init()
    public isShowing: boolean;

    /**
     * Tween props for show/hide
     * override using SetShowProps/SetHideProps
     * Default: uses @see defaultShowProps / @see defaultHideProps
     * @see https://newdocs.phaser.io/docs/3.52.0/Phaser.Types.Tweens.TweenBuilderConfig
     * EDIT: unfortunately Phaser.Types.Tweens.TweenBuilderConfig requires 'targets' 
     *       and we dont have those just yet
     */
    protected showTweenConfig: any;
    protected hideTweenConfig: any;
    protected showTweenTargetProps: any;
    protected hideTweenTargetProps: any;
    protected fromShowTweenTargetProps: any;

    protected animTween: Phaser.Tweens.Tween;


    /**
     * Overrides existing tween props
     * - overriding with supplied props 
     *   - if you supply undefined, existing prop is overriden by undefined
     *   - if you don't supply a prop, and prop exists on local config object, value on local object stays.
     * 
     * NOTE: every local config object starts with default settings (set inside this function [because Mixins])
     * NOTE: use bind on callbacks to bind to caller scope.
     * NOTE: Tween callbacks (eg onStart) are ASYNCHRONOUS! 
     * NOTE: onStart is patched to be called synchronously, if delay=0.
     * @example: 
        this.SetTweenProps(
            // showTweenConfig
            {
                // NOTE: onStart is patched to be called synchronously, if delay=0
                onStart: this.OnShowStart.bind(this),
                
                onComplete: this.OnShowComplete.bind(this),
            },
            // hideTweenConfig
            {
                onStart: this.OnHideStart.bind(this),
            }
        );
     * 
     * @param showTweenConfig tween config, like duration of tween, ease, ..
     * @param hideTweenConfig 
     * @param showTweenTargetProps tween target props config like scale, x, ..
     * @param hideTweenTargetProps 
     */
    public SetTweenProps(
        showTweenConfig: any = undefined,
        hideTweenConfig: any = undefined,
        showTweenTargetProps: any = undefined,
        hideTweenTargetProps: any = undefined,
        fromShowTweenTargetProps: any = undefined
    ): void {
        /**
         * 
         * Set defaults (once)
         * 
         */

        // this.showTweenConfig
        if (this.showTweenConfig == undefined) {
            this.showTweenConfig = {
                ...Showable.defaultShowTweenConfig,
            }
        }
        // this.hideTweenConfig
        if (this.hideTweenConfig == undefined) {
            this.hideTweenConfig = {
                ...Showable.defaultHideTweenConfig,
            }
        }
        // this.showTweenTargetProps
        if (this.showTweenTargetProps == undefined) {
            this.showTweenTargetProps = {
                ...Showable.defaultShowTweenTargetProps,
            }
        }
        // this.hideTweenTargetProps
        if (this.hideTweenTargetProps == undefined) {
            this.hideTweenTargetProps = {
                ...Showable.defaultHideTweenTargetProps,
            }
        }
        // this.fromShowTweenTargetProps
        // EDIT: no defaults for this! leaving it undefined prevents assigning defaults to everything that has these unspecified
        // if (this.fromShowTweenTargetProps == undefined) {
        //     this.fromShowTweenTargetProps = {
        //         ...Showable.defaultFromShowTweenTargetProps,
        //     }
        // }

        /**
         * 
         * apply input params
         * 
         */

        // showTweenConfig
        if (showTweenConfig != undefined) {
            this.showTweenConfig = {
                ...this.showTweenConfig,
                ...showTweenConfig,
            }
        }
        // hideTweenConfig
        if (hideTweenConfig != undefined) {
            this.hideTweenConfig = {
                ...this.hideTweenConfig,
                ...hideTweenConfig,
            }
        }
        // showTweenTargetProps
        if (showTweenTargetProps != undefined) {
            this.showTweenTargetProps = {
                ...showTweenTargetProps,
            };
        }
        // hideTweenTargetProps
        if (hideTweenTargetProps != undefined) {
            this.hideTweenTargetProps = {
                ...hideTweenTargetProps,
            };
        }
        // fromShowTweenTargetProps
        if (fromShowTweenTargetProps != undefined) {
            this.fromShowTweenTargetProps = {
                ...fromShowTweenTargetProps,
            };
        }
    }

    public Show(animated: boolean = true): void {
        // console.log("[mixin] Show", this.name);

        // ensure at least default props
        if (this.showTweenConfig == undefined) {
            this.SetTweenProps();
        }

        // // apply 'from' props to target before starting to move to 'to' props
        // if (useShowTweenTargetProps) {
        //     if (this.fromShowTweenTargetProps == undefined) {
        //         this.SetTweenProps();
        //     }
        //     for (let prop in this.showTweenTargetProps) {
        //         this[prop] = this.showTweenTargetProps[prop];
        //     }
        // }

        // in base or another mixin
        // @ts-ignore
        // if (this.OnShowStart != undefined) {
        //     // @ts-ignore
        //     this.OnShowStart();
        // }

        // apply 'fromShowTweenTargetProps
        if (this.fromShowTweenTargetProps) {
            // console.log(this.fromShowTweenTargetProps);
            for (let prop in this.fromShowTweenTargetProps) {
                this[prop] = this.fromShowTweenTargetProps[prop];
            }
        }

        // kill before animate, since can call animate:true, animate:false, and while tween is going, false view state will be override by true
        this.scene.tweens.killTweensOf(this);

        // if animate
        // const genericHideDuration = OptionsDataManager.GetTiming(AppTimings.genericHideDuration);
        if (animated) {
            // patching config:
            // > run onStart synchronously, if defined, and delay=0 (else it runs asynchronously)
            let patchedShowTweenConfig;
            if (this.showTweenConfig.onStart != undefined) {
                if (this.showTweenConfig.delay == undefined || this.showTweenConfig.delay == 0) {
                    this.showTweenConfig.onStart(animated, this.showTweenConfig.onStartParams);
                    patchedShowTweenConfig = { onStart: undefined };
                }
            }

            // EDIT: unsure i want this .. 
            // for (let prop in this.hideTweenTargetProps) {
            //     this[prop] = this.hideTweenTargetProps[prop];
            // }
            // this.animTween = 
            this.scene.tweens.add({
                ...this.showTweenConfig,
                ...patchedShowTweenConfig,
                ...this.showTweenTargetProps,
                targets: this,
            });
        }
        else {
            for (let prop in this.showTweenTargetProps) {
                this[prop] = this.showTweenTargetProps[prop];
            }
            // run callbacks if defined
            this.showTweenConfig.onStart?.(animated, this.showTweenConfig.onStartParams);
            this.showTweenConfig.onComplete?.();
        }
        this.isShowing = true;

        // console.log(this.IsInteractive);
    }

    public Hide(animated: boolean = true): void {
        // console.log("[mixin] Hide", animated, this.name);
        // console.log(animated, this.hideTweenConfig);

        // ensure at least default props
        if (this.hideTweenConfig == undefined) {
            this.SetTweenProps();
        }

        // in base or another mixin
        // @ts-ignore
        // if (this.OnHideStart != undefined) {
        //     // @ts-ignore
        //     this.OnHideStart();
        // }

        this.scene.tweens.killTweensOf(this);

        // if animate
        if (animated) {
            // patching config:
            // > run onStart synchronously, if defined, and delay=0 (else it runs asynchronously)
            let patchedHideTweenConfig;
            if (this.hideTweenConfig.onStart != undefined) {
                if (this.hideTweenConfig.delay == undefined || this.hideTweenConfig.delay == 0) {
                    this.hideTweenConfig.onStart(animated, this.hideTweenConfig.onStartParams);
                    patchedHideTweenConfig = { onStart: undefined };
                }
            }

            // this.animTween = 
            this.scene.tweens.add({
                ...this.hideTweenConfig,
                ...patchedHideTweenConfig,
                ...this.hideTweenTargetProps,
                targets: this,
            });
        }
        // don't animate
        else {
            for (let prop in this.hideTweenTargetProps) {
                this[prop] = this.hideTweenTargetProps[prop];
            }
            // run callbacks if defined
            this.hideTweenConfig.onStart?.(animated, this.hideTweenConfig.onStartParams);
            this.hideTweenConfig.onComplete?.();
        }
        this.isShowing = false;
    }

    public IsShowing(): boolean {
        return this.isShowing;
    }
}